import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import size from 'lodash/fp/size';
import noop from 'lodash/fp/noop';

import classNames from 'classnames';

import { PARAMETERS_PATH, SYSTEM_STATE_NOTIFICATIONS_PATH } from '~/features/base/constants/routes';
import { connect } from 'react-redux';
import { isDiagLogFileManagerSelector } from '~/features/user/selectors/permissionSelectors';
import {
    canReadControlDevicesSelector,
    controlDeviceEligibleDeliverablesSizeSelector,
    controlDeviceEligibleFileVersionsSelector,
    controlDeviceEligiblePackageVersionsSelector,
    getSelectedTabIdSelector,
} from '~/features/devices/selectors/controlDeviceSelectors';
import DeviceSystemParametersDialog from '~/features/devices/components/dialogs/DeviceSystemParametersDialog';
import { canCreateDeviceActionsSelector } from '~/features/deviceLogs/selectors/deviceActionSelectors';
import { canReadSysParamDefinitionsSelector } from '~/features/sysParams/selectors/sysParamDefinitionSelectors';
import {
    ADAPT_ORDERS,
    DELIVERABLES,
    DETAILS,
    FILES,
    HISTORY,
    LOGS,
    PACKAGES,
    REPORT,
    UPDATE_EVENTS,
} from '~/features/devices/constants/deviceDetailsTabs';
import { setSelectedTabDeviceDetailsSidebar } from '~/features/devices/actions/ui/controlDeviceDetailsActions';
import DeviceSystemStatesDialog from '~/features/devices/components/dialogs/DeviceSystemStatesDialog';
import { CM4 } from '~/features/devices/constants/deviceTypes';
import { env } from '~/env';

export class DeviceDetailsTabs extends PureComponent {
    constructor() {
        super();
        this.state = {
            showSystemParametersDialog: false,
            showSystemStatesDialog: false,
        };
    }

    renderNavigationItem = (tabId, currentTabId, label, badgeValue, action) => {
        const className = {
            active: currentTabId === tabId,
        };
        return (
            <li key={tabId} className={classNames(className)}>
                <a class='nav-link'
                   onClick={action ? action : () => this.props.setSelectedTabDeviceDetailsSidebar(tabId)}>
                    <FormattedMessage id={label}/>
                    {
                        badgeValue ? <span className='badge margin-left-5'>{badgeValue}</span> : null
                    }
                </a>
            </li>
        );
    };

    renderNavigation = () => {
        const {
            canReadSysParamDefinitions,
            controlDeviceEligiblePackageVersions,
            controlDeviceEligibleFileVersions,
            controlDeviceEligibleCM4Size,
            serialNumber,
            selectedTabId,
            deviceType,
        } = this.props;

        const { showSystemParametersDialog, showSystemStatesDialog } = this.state;
        const amountEligiblePackageVersions = size(controlDeviceEligiblePackageVersions);
        const amountEligibleFileVersions = size(controlDeviceEligibleFileVersions);
        const amountEligibleCM4Versions = controlDeviceEligibleCM4Size;
        const showAdaptOrdersTab = deviceType === CM4 && env.runtimeConfig.showAdaptOrder;

        return (
            <ul className='nav nav-pills'>
                {this.renderNavigationItem(DETAILS, selectedTabId, 'intl-msg:controlDeviceDetails')}
                {deviceType !== CM4 && this.renderNavigationItem(PACKAGES, selectedTabId, 'intl-msg:packages',
                    amountEligiblePackageVersions)}
                {deviceType !== CM4 && this.renderNavigationItem(FILES, selectedTabId, 'intl-msg:files',
                    amountEligibleFileVersions)}
                {deviceType === CM4 && this.renderNavigationItem(DELIVERABLES, selectedTabId, 'intl-msg:deliverables', amountEligibleCM4Versions)}
                {this.renderNavigationItem(UPDATE_EVENTS, selectedTabId, 'intl-msg:updateEvents')}
                {this.renderNavigationItem(REPORT, selectedTabId, 'intl-msg:report')}
                {this.renderNavigationItem(LOGS, selectedTabId, 'intl-msg:logs')}
                {showAdaptOrdersTab &&
                    this.renderNavigationItem(ADAPT_ORDERS, selectedTabId, 'Adapt Orders')}
                {deviceType !== CM4 && canReadSysParamDefinitions && this.renderNavigationItem(PARAMETERS_PATH, undefined, 'intl-msg:sysParams', undefined, this.toggleSystemParametersDialog)}
                {deviceType !== CM4 && showSystemParametersDialog &&
                    <DeviceSystemParametersDialog serialNumber={serialNumber}
                                                  toggleSystemParametersDialog={this.toggleSystemParametersDialog}/>}
                {this.renderNavigationItem(SYSTEM_STATE_NOTIFICATIONS_PATH, undefined, 'intl-msg:apps.deviceState.title', undefined, this.toggleSystemStatesDialog)}


                {showSystemStatesDialog && <DeviceSystemStatesDialog serialNumber={serialNumber}
                                                                     deviceType={deviceType}
                                                                     toggleSystemStatesDialog={this.toggleSystemStatesDialog}/>}
                {this.renderNavigationItem(HISTORY, selectedTabId, 'intl-msg:history')}
            </ul>
        );
    };

    toggleSystemParametersDialog = () => {
        this.setState({ showSystemParametersDialog: !this.state.showSystemParametersDialog });
    };

    toggleSystemStatesDialog = () => {
        this.setState({ showSystemStatesDialog: !this.state.showSystemStatesDialog });
    };

    render() {
        const navigation = this.renderNavigation();
        return (
            <div className='device-details-tabs row margin-bottom-20'>
                <div className='col-12 height-100pct'>
                    {navigation}
                </div>
            </div>
        );
    }
}

export const mapStateToProps = (state, ownProps) => {
    return {
        isDeviceLogsUser: isDiagLogFileManagerSelector(state),
        controlDeviceEligiblePackageVersions: controlDeviceEligiblePackageVersionsSelector(state, ownProps),
        controlDeviceEligibleFileVersions: controlDeviceEligibleFileVersionsSelector(state, ownProps),
        controlDeviceEligibleCM4Size: controlDeviceEligibleDeliverablesSizeSelector(state),
        canReadControlDevices: canReadControlDevicesSelector(state),
        canCreateDeviceActions: canCreateDeviceActionsSelector(state),
        canReadSysParamDefinitions: canReadSysParamDefinitionsSelector(state),
        selectedTabId: getSelectedTabIdSelector(state),
    };
};

export const mapDispatchToProps = (dispatch) => {
    return {
        setSelectedTabDeviceDetailsSidebar: (tabId) => {
            dispatch(setSelectedTabDeviceDetailsSidebar(tabId));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(DeviceDetailsTabs);

DeviceDetailsTabs.defaultProps = {
    // props
    serialNumber: '',
    selectedTabId: '',
    isDeviceLogsUser: false,
    canReadSysParamDefinitions: false,
    controlDeviceEligiblePackageVersions: {},
    controlDeviceEligibleFileVersions: {},
    setSelectedTabDeviceDetailsSidebar: noop,
};

DeviceDetailsTabs.propTypes = {
    // props
    serialNumber: PropTypes.string,
    selectedTabId: PropTypes.string,
    isDeviceLogsUser: PropTypes.bool,
    canReadSysParamDefinitions: PropTypes.bool,
    controlDeviceEligiblePackageVersions: PropTypes.object,
    controlDeviceEligibleFileVersions: PropTypes.object,
    setSelectedTabDeviceDetailsSidebar: PropTypes.func,
};
